<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { X } from "@lucide/vue"
import { reactiveOmit } from "@vueuse/core"
import {
  DialogClose,
  DialogContent,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui"
import { COARSE_POINTER_MQ, useMediaQuery } from "@/composables/useMediaQuery"
import { cn } from "@/lib/utils"
import DialogOverlay from "./DialogOverlay.vue"

defineOptions({
  inheritAttrs: false,
})

// elevated: модалка живёт на верхнем слое (z-[100]) — всегда поверх обычных
// модалок/шторок (z-50). Для глобальной подтверждалки выхода, которую вызывают
// ИЗ другой модалки: при равном z-50 она проваливается под неё (правило frontend-modals).
const props = withDefaults(defineProps<DialogContentProps & { class?: HTMLAttributes["class"], showCloseButton?: boolean, elevated?: boolean }>(), {
  showCloseButton: true,
  elevated: false,
})
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, "class")

const forwarded = useForwardPropsEmits(delegatedProps, emits)

// Проектная адаптация: на тач-устройствах reka-ui не должен автофокусить
// первый элемент при открытии — открывается клавиатура и закрывает поле
// (фокус-политика, правило frontend-page-layout).
const isCoarsePointer = useMediaQuery(COARSE_POINTER_MQ)

function onOpenAutoFocus(event: Event) {
  if (isCoarsePointer.value) event.preventDefault()
}
</script>

<template>
  <DialogPortal>
    <DialogOverlay :class="elevated ? 'z-[100]' : undefined" />
    <DialogContent
      data-slot="dialog-content"
      v-bind="{ ...$attrs, ...forwarded }"
      @open-auto-focus="onOpenAutoFocus"
      :class="
        cn(
          // Проектная адаптация: на мобилке (< sm) модалка — bottom sheet,
          // прижата к низу без нижних закруглений; на desktop — по центру.
          // outline-none: фокус-трап ставит фокус на сам контейнер (не
          // интерактивный элемент) — обводка вокруг всей модалки не нужна.
          // Тело модалки белое (surface); футер (DialogFooter) — бежевый (ADDENDUM §J).
          // overflow-y-auto + кап высоты: высокий контент (каскад ролей) не уезжает
          // за край вьюпорта, а скроллится внутри модалки. Кап — как у SheetContent
          // (92dvh мобилка, вьюпорт минус зазор на десктопе). overflow-x-clip: без
          // явной оси X у overflow-y:auto вторая ось трактуется как auto → лишний
          // горизонтальный скролл при любом X-переполнении (модалка никогда не едет вбок).
          'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed z-50 grid w-full gap-4 overflow-x-clip overflow-y-auto border p-6 shadow-lg duration-200 outline-none',
          'max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:rounded-t-xl max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom',
          'sm:top-[50%] sm:left-[50%] sm:max-h-[calc(100dvh-4rem)] sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
          elevated && 'z-[100]',
          props.class,
        )"
    >
      <slot />

      <DialogClose
        v-if="showCloseButton"
        data-slot="dialog-close"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      >
        <X />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
